import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import type { IDefinitionDescriptions } from '../types';
import {
  ManifestComponentExtensionType,
  MetadataByExtensionType,
} from '../stores';

interface DebugPanelOverrideData {
  componentType: string;
  widgetDescription: string;
  styleDescriptions: Record<string, string>;
  settingsDescriptions: Record<string, string>;
  presetDescriptions: Record<string, string>;
}

export class DefinitionDescriptionsPlatformService extends AbstractComponentsPlatformService {
  getByType(type: string): IDefinitionDescriptions | null {
    const manifest = this.manifestStore.get(type, { allowPending: true });

    if (!manifest?.metadata?.statics) {
      return null;
    }

    // TODO: fix as
    const metadata =
      manifest.metadata as MetadataByExtensionType[ManifestComponentExtensionType.OOI];

    const statics = metadata!.statics;

    const cssCustomProperties: Record<string, { description: string }> = {};
    const presets: Record<string, { description: string }> = {};
    const data: Record<string, { description: string }> = {};
    const description: string | undefined = statics.description;

    if (statics.styleParams) {
      Object.entries(statics.styleParams).forEach(([key, param]) => {
        if (param?.metadata?.ai?.description) {
          cssCustomProperties[key] = {
            description: param.metadata.ai.description,
          };
        }
      });
    }

    if (statics.settingsParams) {
      Object.entries(statics.settingsParams).forEach(([key, param]) => {
        if (param?.metadata?.ai?.description) {
          data[key] = { description: param.metadata.ai.description };
        }
      });
    }

    if (statics.presetsPanel) {
      statics.presetsPanel.forEach((preset) => {
        if (preset?.ai?.description) {
          presets[preset.id] = {
            description: preset.ai.description,
          };
        }
      });
    }

    const result: IDefinitionDescriptions = {
      cssCustomProperties,
      data,
      presets,
      description,
    };

    this.applyLocalStorageOverrides(type, result);

    return result;
  }

  private applyLocalStorageOverrides(
    type: string,
    result: IDefinitionDescriptions,
  ): void {
    if (!this.experimentsAPI.enabled('specs.odeditor.od_enableAIDebugPanel')) {
      return;
    }

    const debugPanelOverride = sessionStorage.getItem('component-description');
    if (!debugPanelOverride) {
      return;
    }

    const debugPanelOverrideData: DebugPanelOverrideData =
      JSON.parse(debugPanelOverride);

    if (debugPanelOverrideData.componentType !== type) {
      return;
    }

    if (debugPanelOverrideData.widgetDescription) {
      result.description = debugPanelOverrideData.widgetDescription;
    }

    if (debugPanelOverrideData.styleDescriptions) {
      Object.keys(debugPanelOverrideData.styleDescriptions).forEach((key) => {
        result.cssCustomProperties[key] = {
          description: debugPanelOverrideData.styleDescriptions[key],
        };
      });
    }

    if (debugPanelOverrideData.settingsDescriptions) {
      Object.keys(debugPanelOverrideData.settingsDescriptions).forEach(
        (key) => {
          result.data[key] = {
            description: debugPanelOverrideData.settingsDescriptions[key],
          };
        },
      );
    }

    if (debugPanelOverrideData.presetDescriptions) {
      Object.keys(debugPanelOverrideData.presetDescriptions).forEach((id) => {
        result.presets[id] = {
          description: debugPanelOverrideData.presetDescriptions[id],
        };
      });
    }
  }
}
